import { useEffect, useRef, useState } from 'react';

export default function usePdfRender({ pdf, numPages, currentPage, effectiveScale, canvasRef1, canvasRef2 }) {
	const [rendering, setRendering] = useState(false);
	const renderTasksRef = useRef({ p1: null, p2: null });

	const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

	const cancelRenders = () => {
		const { p1, p2 } = renderTasksRef.current;
		try {
			p1 && p1.cancel();
		} catch {}
		try {
			p2 && p2.cancel();
		} catch {}
		renderTasksRef.current = { p1: null, p2: null };
	};

	const renderPageToCanvas = async (pageNum, canvas) => {
		if (!pdf || !canvas) return null;
		const page = await pdf.getPage(pageNum);
		const viewport = page.getViewport({ scale: effectiveScale });

		// CSS
		canvas.style.width = `${viewport.width}px`;
		canvas.style.height = `${viewport.height}px`;

		// Buffer
		const pxW = Math.floor(viewport.width * dpr);
		const pxH = Math.floor(viewport.height * dpr);
		if (canvas.width !== pxW) canvas.width = pxW;
		if (canvas.height !== pxH) canvas.height = pxH;

		const ctx = canvas.getContext('2d');
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		return page.render({ canvasContext: ctx, viewport });
	};

	useEffect(() => {
		if (!pdf) return;
		let mounted = true;
		(async () => {
			setRendering(true);
			cancelRenders();
			let t1 = null;
			let t2 = null;

			try {
				// Start both renders
				t1 = await renderPageToCanvas(currentPage, canvasRef1.current);
				renderTasksRef.current.p1 = t1;

				if (currentPage + 1 <= numPages) {
					t2 = await renderPageToCanvas(currentPage + 1, canvasRef2.current);
					renderTasksRef.current.p2 = t2;
				} else if (canvasRef2.current) {
					const c2 = canvasRef2.current;
					const ctx2 = c2.getContext('2d');
					ctx2 && ctx2.clearRect(0, 0, c2.width, c2.height);
					c2.style.width = '0px';
					c2.style.height = '0px';
					c2.width = 0;
					c2.height = 0;
				}

				// Wait for both to complete before marking as done
				try {
					if (t1) await t1.promise;
				} catch {}
				try {
					if (t2) await t2.promise;
				} catch {}
			} finally {
				if (mounted) setRendering(false);
			}
		})();

		return () => {
			mounted = false;
			cancelRenders();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pdf, numPages, currentPage, effectiveScale, dpr]);

	return { rendering };
}
